// Help Center Page
// Comprehensive self-service help with FAQs, video tutorials, and guides

import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Search, Book, Video, MessageCircle, ChevronDown, ChevronUp, 
  ChevronRight, Play, FileText, Settings, Users, BarChart3,
  Code, Palette, Bell, Database, ArrowLeft, ExternalLink, X
} from 'lucide-react';

const HelpCenterPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [activeCategory, setActiveCategory] = useState('getting-started');
  const [selectedVideo, setSelectedVideo] = useState(null);

  const categories = [
    { id: 'getting-started', name: 'Getting Started', icon: Book },
    { id: 'leads', name: 'Managing Leads', icon: Users },
    { id: 'widget', name: 'Chat Widget', icon: Code },
    { id: 'integrations', name: 'CMS & Integrations', icon: Database },
    { id: 'branding', name: 'Branding & Design', icon: Palette },
    { id: 'notifications', name: 'Notifications', icon: Bell },
    { id: 'analytics', name: 'Analytics & Reports', icon: BarChart3 },
    { id: 'billing', name: 'Billing & Account', icon: Settings },
  ];

  const videoTutorials = [
    { 
      id: 1, 
      title: 'Quick Start Guide', 
      duration: '3:45', 
      thumbnail: 'https://customer-assets.emergentagent.com/job_intake-hub-4/artifacts/s23w2pel_Onboarding.gif',
      description: 'Get up and running with IntakePal in under 5 minutes',
      embedUrl: 'https://share.synthesia.io/embeds/videos/5718449a-2427-440e-991a-242ce078ab14'
    },
    { 
      id: 2, 
      title: 'Installing the Chat Widget', 
      duration: '2:30', 
      thumbnail: 'https://customer-assets.emergentagent.com/job_intake-hub-4/artifacts/ij703o0z_Installing%20Chat%20Wizard.gif',
      description: 'Step-by-step widget installation for any website',
      embedUrl: 'https://share.synthesia.io/embeds/videos/a7fb3290-b4a0-4508-b0f5-b9cdf53b35ae'
    },
    { 
      id: 3, 
      title: 'Understanding Lead Scores', 
      duration: '4:15', 
      thumbnail: 'https://customer-assets.emergentagent.com/job_intake-hub-4/artifacts/jm60s3p4_Understanding%20your%20Lead%20Scores.gif',
      description: 'How to prioritize leads using lead scoring',
      embedUrl: 'https://share.synthesia.io/embeds/videos/31141cef-5874-48df-8353-5b0a7550d58a'
    },
    { 
      id: 4, 
      title: 'Exporting to Your CMS', 
      duration: '3:00', 
      thumbnail: 'https://customer-assets.emergentagent.com/job_intake-hub-4/artifacts/i609tzdd_Exporting%20to%20your%20CMS.gif',
      description: 'Export leads to Clio, Filevine, and 20+ systems',
      embedUrl: 'https://share.synthesia.io/embeds/videos/039500c3-a165-4fd1-a525-4b193ff2d33f'
    },
    { 
      id: 5, 
      title: 'Customizing Your Branding', 
      duration: '2:45', 
      thumbnail: 'https://customer-assets.emergentagent.com/job_intake-hub-4/artifacts/9nwx2xlj_Customize%20Your%20Branding.gif',
      description: 'Make IntakePal match your firm\'s identity',
      embedUrl: 'https://share.synthesia.io/embeds/videos/9e8424fb-b567-44f8-a2b6-d509a48391fc'
    },
    { 
      id: 6, 
      title: 'Setting Up Notifications', 
      duration: '2:15', 
      thumbnail: 'https://customer-assets.emergentagent.com/job_intake-hub-4/artifacts/yh2vd1sg_Setting%20up%20your%20Notifications.gif',
      description: 'Never miss a lead with email and SMS alerts',
      embedUrl: 'https://share.synthesia.io/embeds/videos/4774d63a-ee70-4c80-9851-b768094983fa'
    },
  ];

  const faqs = {
    'getting-started': [
      {
        q: 'How do I get started with IntakePal?',
        a: `Getting started is easy! Follow these steps:

1. **Complete the Setup Wizard** - Go to Settings → Setup Wizard
2. **Customize your branding** - Add your logo and brand colors
3. **Install the widget** - Copy the embed code to your website
4. **Set up notifications** - Configure email/SMS alerts for new leads

The entire process takes about 10 minutes. Your first lead could come in today!`
      },
      {
        q: 'What is the Setup Wizard?',
        a: `The Setup Wizard is a 3-step onboarding process that helps you configure IntakePal:

**Step 1: Branding** - Upload your logo and choose your brand colors
**Step 2: CMS** - Select your case management system for formatted exports
**Step 3: Embed** - Get your widget code to install on your website

You can access it anytime from Settings → Setup Wizard.`
      },
      {
        q: 'How long does setup take?',
        a: `Most firms complete setup in **10-15 minutes**. Here's a typical timeline:

- Branding setup: 2-3 minutes
- Widget installation: 5 minutes (depending on your website platform)
- CMS configuration: 1 minute
- Notification setup: 2 minutes

You don't need any technical expertise. Our Setup Wizard guides you through each step.`
      },
    ],
    'leads': [
      {
        q: 'What do the lead scores (A, B, C, D, F) mean?',
        a: `Lead scores help you prioritize which potential clients to contact first based on the completeness and details of their submission:

**A (90-100 points)** - High priority! Detailed information provided. Contact immediately.
**B (70-89 points)** - Good detail provided. Worth prioritizing.
**C (50-69 points)** - Moderate detail. Standard follow-up timing.
**D (30-49 points)** - Limited information provided. May need more details.
**F (0-29 points)** - Minimal information submitted. Review manually.

The score is calculated based on the completeness of information submitted, including contact details, accident information, and documentation provided.`
      },
      {
        q: 'How do I change a lead\'s status?',
        a: `To update a lead's status:

1. Go to **Leads** in the sidebar
2. Click on the lead you want to update
3. In the lead detail view, click the **status dropdown**
4. Choose from: New, Reviewed, Contacted, Consultation Scheduled, Converted, or Closed

You can also mark leads as "Reviewed," "Contacted," or "Converted" directly from the lead card using the quick action buttons.`
      },
      {
        q: 'How do I export leads to my CMS?',
        a: `IntakePal supports 25+ CMS systems. To export:

**Single Lead Export:**
1. Open the lead detail page
2. Click "Export to CMS"
3. Select your CMS format (Clio, Filevine, etc.)
4. Download the CSV file
5. Import into your CMS

**Bulk Export:**
1. Go to Leads page
2. Click "Export All" or select multiple leads
3. Choose your CMS format
4. Download and import

**Pro Tip:** Set your preferred CMS in Settings to automatically receive correctly formatted CSVs with each lead notification email!`
      },
    ],
    'widget': [
      {
        q: 'How do I install the chat widget on my website?',
        a: `Installing the widget is simple:

1. Go to **Settings → Embed Code** (or complete Setup Wizard Step 4)
2. Copy the JavaScript snippet
3. Paste it just before the \`</body>\` tag on your website
4. Save and refresh your page - the widget appears automatically!

**Platform-Specific Guides:**
- **WordPress:** Use a plugin like "Insert Headers and Footers"
- **Squarespace:** Settings → Advanced → Code Injection → Footer
- **Wix:** Settings → Custom Code → Body End
- **Shopify:** Online Store → Themes → Edit Code → theme.liquid`
      },
      {
        q: 'Can I customize how the widget looks?',
        a: `Yes! You can customize:

- **Primary Color** - Match your brand color
- **Logo** - Display your firm's logo in the chat header
- **Welcome Message** - Customize the greeting text
- **Position** - Bottom-right or bottom-left of the page

Go to **Settings → Branding** to make these changes. Preview your changes before saving!`
      },
      {
        q: 'What languages does the widget support?',
        a: `IntakePal supports **30 languages** with automatic detection:

English, Spanish, French, German, Portuguese, Italian, Chinese (Simplified & Traditional), Japanese, Korean, Arabic, Hindi, Russian, Vietnamese, Tagalog, Polish, Dutch, Turkish, Thai, Swedish, Greek, Hebrew, Czech, Romanian, Hungarian, Indonesian, Malay, Danish, Norwegian, and Finnish.

The widget automatically detects the client's language and responds accordingly. Retainer agreements can be generated in both English and the client's language (bilingual PDFs).`
      },
    ],
    'integrations': [
      {
        q: 'Which CMS systems are supported?',
        a: `IntakePal supports **25+ legal CMS systems**:

**Most Popular:**
⭐ Filevine, Clio, MyCase, CASEpeer, Prevail

**Legal Case Management:**
Litify, PracticePanther, Smokeball, Smart Advocate, Needles, CloudLex, Lawmatics, Rocket Matter, CosmoLex, AbacusLaw, PCLaw, Tabs3, TimeSolv, LEAP Legal

**Plaintiff Systems:**
TrialWorks, SimplyConvert

**CRM Systems:**
Salesforce, HubSpot, Zoho CRM, Monday.com

Don't see your system? The "Universal" export includes all fields and works with any system.`
      },
      {
        q: 'How do I connect my calendar for scheduling?',
        a: `IntakePal integrates with popular calendar systems:

**Calendly:**
1. Go to Setup Wizard → Calendar
2. Click "Connect Calendly"
3. Enter your Calendly URL
4. Done! Clients can now book consultations automatically.

**Google Calendar / Outlook:**
1. Go to Setup Wizard → Calendar
2. Click "Connect Google Calendar" or "Connect Outlook"
3. Authorize the connection
4. Select which calendar to use for bookings

Once connected, available time slots appear automatically in the intake flow.`
      },
    ],
    'branding': [
      {
        q: 'How do I add my firm\'s logo?',
        a: `To add your logo:

1. Go to **Settings → Branding** (or Setup Wizard Step 2)
2. Click "Upload Logo"
3. Select your logo file (PNG, JPG, or SVG recommended)
4. Adjust the size if needed
5. Click "Save Changes"

Your logo will appear in:
- The chat widget header
- Email notifications to clients
- Generated retainer agreements

**Tip:** Use a logo with a transparent background for best results.`
      },
      {
        q: 'Can I change the widget colors?',
        a: `Yes! To customize colors:

1. Go to **Settings → Branding**
2. Click the color picker for "Primary Color"
3. Enter your brand's hex code (e.g., #2563EB) or select from the palette
4. Preview the changes in real-time
5. Click "Save Changes"

The primary color affects:
- Widget header background
- Buttons and links
- Accent elements throughout the app`
      },
    ],
    'notifications': [
      {
        q: 'How do I set up email notifications?',
        a: `To receive email alerts for new leads:

1. Go to **Settings → Notifications**
2. Toggle on "Email Notifications"
3. Enter the email addresses to notify (you can add multiple)
4. Choose notification frequency: Instant, Hourly Digest, or Daily Summary
5. Save your settings

Each notification includes:
- Client contact info
- Automated case summary
- Lead score and grade
- CSV attachment formatted for your CMS (if configured)`
      },
      {
        q: 'How do I set up SMS notifications?',
        a: `To receive text message alerts:

1. Go to **Settings → Notifications**
2. Toggle on "SMS Notifications"
3. Enter your mobile phone number
4. Verify your number with the confirmation code
5. Save your settings

**Note:** SMS notifications require Twilio integration. If you see an error about carrier registration, you may need to complete A2P 10DLC registration in your Twilio console for reliable delivery.`
      },
      {
        q: 'How do I connect my Twilio account for SMS?',
        a: `To enable SMS notifications, you need to connect your Twilio account:

1. **Create a Twilio Account** - Sign up at twilio.com if you don't have one
2. Go to **Settings → Integrations** in IntakePal
3. Find the **Twilio** section and click "Connect"
4. Enter your Twilio credentials:
   - **Account SID** - Found on your Twilio Console dashboard
   - **Auth Token** - Found on your Twilio Console dashboard
   - **Phone Number** - Your Twilio phone number (format: +1XXXXXXXXXX)
5. Click "Save" to connect

**Where to find your Twilio credentials:**
- Log in to console.twilio.com
- Your Account SID and Auth Token are on the main dashboard
- Go to Phone Numbers → Manage → Active Numbers to see your phone number`
      },
      {
        q: 'Why are my SMS messages not being delivered?',
        a: `If SMS messages aren't being delivered, check these common issues:

**1. A2P 10DLC Registration (US Only)**
For US phone numbers, carriers require A2P 10DLC registration:
- Go to Twilio Console → Messaging → Compliance → A2P Registration
- Register your business and use case
- This can take 1-2 weeks for approval

**2. Phone Number Format**
- Ensure phone numbers include country code (e.g., +1 for US)
- Format should be: +1XXXXXXXXXX

**3. Twilio Balance**
- Check your Twilio account has sufficient balance
- SMS costs approximately $0.0079 per message

**4. Carrier Filtering**
- Some carriers filter messages that look like spam
- Avoid using ALL CAPS or excessive punctuation
- Include your firm name in messages

**5. Verify Twilio Phone Number**
- Make sure your Twilio number is SMS-enabled
- Check Phone Numbers → Active Numbers in Twilio Console

**Still having issues?** Check the Twilio Console → Monitor → Logs for specific error messages.`
      },
      {
        q: 'What is A2P 10DLC and do I need it?',
        a: `**A2P 10DLC** (Application-to-Person 10-Digit Long Code) is a registration requirement for businesses sending SMS messages in the United States.

**Do you need it?**
- **Yes** if you're sending SMS to US phone numbers
- **No** if you're only sending to non-US numbers

**Why is it required?**
- US carriers (AT&T, T-Mobile, Verizon) require it to reduce spam
- Unregistered messages may be blocked or filtered

**How to register:**
1. Go to Twilio Console → Messaging → Compliance
2. Click "Register for A2P 10DLC"
3. Complete your Business Profile
4. Register your Campaign (use case: "Customer Care" or "Account Notifications")
5. Wait for approval (typically 1-2 weeks)

**Cost:** Twilio charges a small monthly fee (~$2/month for the campaign).

**Tip:** Start this process early - your SMS notifications won't be reliable until registration is complete.`
      },
      {
        q: 'How much does SMS cost?',
        a: `SMS costs depend on your Twilio usage:

**Twilio Pricing (approximate):**
- Outbound SMS (US): ~$0.0079 per message
- Inbound SMS (US): ~$0.0079 per message
- Phone number rental: ~$1.15/month

**IntakePal Usage:**
- 1 SMS sent per new lead notification (if enabled)
- 1 SMS for follow-up sequences (if configured)

**Example monthly cost:**
- 50 leads/month with SMS notifications = ~$0.40/month
- Plus phone number rental = ~$1.55/month total

**To manage costs:**
- Use email notifications for less urgent alerts
- Reserve SMS for high-priority "A" grade leads only
- Monitor usage in your Twilio Console`
      },
    ],
    'analytics': [
      {
        q: 'Where can I see my intake statistics?',
        a: `Your dashboard shows key metrics at a glance:

- **Total Leads** - All intakes received
- **New Today** - Leads received today
- **High Priority** - Leads scored B or higher
- **Conversion Rate** - Percentage of leads converted to clients

For detailed analytics, click on any stat card to see breakdowns by:
- Time period (daily, weekly, monthly)
- Lead source
- Case type
- Lead grade distribution`
      },
    ],
    'billing': [
      {
        q: 'How much does IntakePal cost?',
        a: `IntakePal Pro is **$179.99/month** with a **7-day free trial**.

**What's included:**
- Unlimited leads
- All 30 languages
- Automated case summaries and lead scoring
- 25+ CMS integrations
- Email and SMS notifications
- Bilingual retainer agreements
- Custom branding
- Priority support

No long-term contracts. Cancel anytime.`
      },
      {
        q: 'How do I update my payment method?',
        a: `To update your payment method:

1. Go to **Settings → Subscription**
2. Click "Manage Billing"
3. Click "Update Payment Method"
4. Enter your new card details
5. Click "Save"

You can also view your billing history and download invoices from this page.`
      },
      {
        q: 'How do I cancel my subscription?',
        a: `We're sorry to see you go! To cancel:

1. Go to **Settings → Subscription**
2. Click "Cancel Subscription"
3. Tell us why you're leaving (optional)
4. Confirm cancellation

Your account will remain active until the end of your current billing period. You can reactivate anytime.`
      },
    ],
  };

  const filteredFaqs = faqs[activeCategory]?.filter(faq => 
    faq.q.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.a.toLowerCase().includes(searchQuery.toLowerCase())
  ) || [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 text-white">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <Link to="/dashboard" className="inline-flex items-center text-teal-100 hover:text-white mb-6">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Link>
          <h1 className="text-3xl font-bold mb-3">Help Center</h1>
          <p className="text-teal-100 text-lg mb-6">Find answers, watch tutorials, and get the most out of IntakePal</p>
          
          {/* Search */}
          <div className="relative max-w-2xl">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
            <input
              type="text"
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-4 rounded-xl text-slate-900 text-lg focus:outline-none focus:ring-2 focus:ring-teal-300"
            />
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-8">
        {/* Video Tutorials Section */}
        <section className="mb-12" id="videos">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-slate-900">Video Tutorials</h2>
            <span className="text-sm text-slate-500">{videoTutorials.length} videos</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {videoTutorials.map((video) => (
              <div 
                key={video.id}
                onClick={() => setSelectedVideo(video)}
                className="bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                data-testid={`video-tutorial-${video.id}`}
              >
                <div className="aspect-video bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center relative overflow-hidden">
                  {video.thumbnail ? (
                    <img 
                      src={video.thumbnail} 
                      alt={video.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="text-5xl">{video.thumbnailEmoji || '🎬'}</span>
                  )}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-lg">
                      <Play className="w-6 h-6 text-teal-600 ml-1" />
                    </div>
                  </div>
                  <span className="absolute bottom-2 right-2 bg-black/70 text-white text-xs px-2 py-1 rounded">
                    {video.duration}
                  </span>
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-slate-900 mb-1">{video.title}</h3>
                  <p className="text-sm text-slate-500">{video.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* FAQ Section */}
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-6">Frequently Asked Questions</h2>
          
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Categories Sidebar */}
            <div className="lg:w-64 flex-shrink-0">
              <div className="bg-white rounded-xl border border-slate-200 p-2 sticky top-4">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg text-left transition-colors ${
                      activeCategory === cat.id 
                        ? 'bg-teal-50 text-teal-700' 
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    <cat.icon className="w-5 h-5" />
                    <span className="font-medium">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ List */}
            <div className="flex-1">
              <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
                {filteredFaqs.length === 0 ? (
                  <div className="p-8 text-center">
                    <p className="text-slate-500">No questions found matching your search.</p>
                  </div>
                ) : (
                  filteredFaqs.map((faq, idx) => (
                    <div key={idx} className="p-0">
                      <button
                        onClick={() => setExpandedFaq(expandedFaq === idx ? null : idx)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
                      >
                        <span className="font-medium text-slate-900 pr-4">{faq.q}</span>
                        {expandedFaq === idx ? (
                          <ChevronUp className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        )}
                      </button>
                      {expandedFaq === idx && (
                        <div className="px-5 pb-5">
                          <div className="prose prose-sm prose-slate max-w-none">
                            <p className="text-slate-600 whitespace-pre-wrap">{faq.a}</p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </section>

        {/* Still Need Help */}
        <section className="mt-12">
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold text-white mb-3">Still have questions?</h2>
            <p className="text-slate-300 mb-6">Our support team is available 24/7 to help you</p>
            <button className="inline-flex items-center gap-2 px-6 py-3 bg-teal-500 text-white rounded-lg font-medium hover:bg-teal-600 transition-colors">
              <MessageCircle className="w-5 h-5" />
              Chat with Support
            </button>
          </div>
        </section>
      </div>

      {/* Video Modal */}
      {selectedVideo && (
        <div 
          className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4"
          onClick={() => setSelectedVideo(null)}
        >
          <div 
            className="bg-white rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-slate-900">{selectedVideo.title}</h3>
              <button 
                onClick={() => setSelectedVideo(null)}
                className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="close-video-modal"
              >
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            
            {/* Video Player */}
            <div className="relative overflow-hidden aspect-video">
              <iframe 
                src={selectedVideo.embedUrl}
                loading="lazy"
                title={`IntakePal Tutorial - ${selectedVideo.title}`}
                allowFullScreen
                allow="encrypted-media; fullscreen; microphone; screen-wake-lock;"
                className="absolute w-full h-full top-0 left-0 border-0"
              />
            </div>
            
            {/* Modal Footer */}
            <div className="p-4 bg-slate-50">
              <p className="text-slate-600 text-sm">{selectedVideo.description}</p>
              <p className="text-slate-500 text-xs mt-2">
                Duration: {selectedVideo.duration}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HelpCenterPage;
